import { useState } from "react";
import { TrendingUp, PieChart, ShoppingBag, Lightbulb, Truck, Trophy, Filter, Maximize2 } from "lucide-react";
import { Loader } from "@/components/ui/Loader";
import { Modal }        from "@/components/ui/Modal";
import { formatCurrency } from "@/utils/format";
import { FornecedorStat } from "../chartUtils";
import { PedidosModalContent, ModalEntityTitle } from "./PedidosModalContent";
import { cn } from "@/utils/cn";

/* ─── Cores por posição ──────────────────────────────────────────────────── */
const ROW_COLORS = [
  "#10b981", // 1º – esmeralda
  "#1e293b", // 2º – slate-900
  "#f97316", // 3º – laranja
];
const DEFAULT_COLOR = "#94a3b8"; // demais

function rowColor(i: number) {
  return i < ROW_COLORS.length ? ROW_COLORS[i] : DEFAULT_COLOR;
}

/* ─── Medalha / ranking ──────────────────────────────────────────────────── */
function RankBadge({ rank }: { rank: number }) {
  if (rank === 1) {
    return (
      <span className="flex items-center justify-center w-7 h-7 rounded-full bg-amber-50">
        <Trophy className="w-4 h-4 text-amber-500" />
      </span>
    );
  }
  const bg = rank === 2 ? "bg-slate-100 text-slate-600"
           : rank === 3 ? "bg-orange-50 text-orange-500"
           : "bg-slate-50 text-slate-400";
  return (
    <span className={cn("flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold", bg)}>
      {rank}
    </span>
  );
}

/* ─── Componente principal ───────────────────────────────────────────────── */
interface Props { data: FornecedorStat[]; loading: boolean; }

export function RankingFornecedores({ data, loading }: Props) {
  const [selected,  setSelected]  = useState<FornecedorStat | null>(null);
  const [expanded,  setExpanded]  = useState(false);

  /* ── Estatísticas ── */
  const total     = data.reduce((s, f) => s + f.valor, 0);
  const top3total = data.slice(0, 3).reduce((s, f) => s + f.valor, 0);
  const top3pct   = total > 0 ? Math.round((top3total / total) * 100) : 0;
  const maxValor  = data[0]?.valor ?? 1;
  const top1      = data[0];

  /* ── Controle de exibição (expandido mostra todos; normal top 7) ── */
  const visible = expanded ? data : data.slice(0, 7);

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <p className="text-sm font-semibold text-slate-700 mb-1">Ranking de Fornecedores</p>
        <div className="h-40 flex items-center justify-center">
          <Loader size="md" label="Carregando ranking…" />
        </div>
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <p className="text-sm font-semibold text-slate-700 mb-1">Ranking de Fornecedores</p>
        <div className="h-40 flex items-center justify-center text-sm text-slate-400">
          Nenhum fornecedor encontrado no período.
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">

        {/* ── Cabeçalho ── */}
        <div className="flex items-start justify-between px-5 pt-5 pb-3">
          <div>
            <p className="text-base font-bold text-slate-800">Ranking de Fornecedores</p>
            <p className="text-xs text-slate-400 mt-0.5">
              {data.length} fornecedor{data.length === 1 ? "" : "es"} do período por valor total comprado
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              title="Filtrar"
            >
              <Filter className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setExpanded((v) => !v)}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              title={expanded ? "Recolher" : "Expandir"}
            >
              <Maximize2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* ── Stats ── */}
        <div className="grid grid-cols-3 gap-3 px-5 pb-4">
          <div className="flex items-center gap-2.5 bg-emerald-50 rounded-xl px-3 py-2.5">
            <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <TrendingUp className="w-4 h-4 text-emerald-600" />
            </div>
            <div>
              <p className="text-[10px] text-slate-500 leading-none mb-0.5">Total comprado</p>
              <p className="text-sm font-bold text-emerald-600 leading-none">
                {formatCurrency(total, true)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2.5 bg-blue-50 rounded-xl px-3 py-2.5">
            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
              <PieChart className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <p className="text-[10px] text-slate-500 leading-none mb-0.5">Top 3 fornecedores</p>
              <p className="text-sm font-bold text-blue-600 leading-none">{top3pct}% do total</p>
            </div>
          </div>
          <div className="flex items-center gap-2.5 bg-slate-50 rounded-xl px-3 py-2.5">
            <div className="w-8 h-8 rounded-lg bg-slate-200 flex items-center justify-center flex-shrink-0">
              <ShoppingBag className="w-4 h-4 text-slate-600" />
            </div>
            <div>
              <p className="text-[10px] text-slate-500 leading-none mb-0.5">Fornecedores</p>
              <p className="text-sm font-bold text-slate-700 leading-none">{data.length}</p>
            </div>
          </div>
        </div>

        {/* ── Tabela ── */}
        <div className="px-5">
          {/* Cabeçalho da tabela */}
          <div className="grid grid-cols-[auto_1fr_auto_auto] gap-x-3 items-center pb-1.5 border-b border-slate-100">
            <div className="w-7" />
            <div />
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide text-right whitespace-nowrap pr-2">
              Valor comprado
            </p>
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide text-right w-12">
              % do total
            </p>
          </div>

          {/* Linhas */}
          <div className="divide-y divide-slate-50">
            {visible.map((f, i) => {
              const pct   = total > 0 ? (f.valor / total) * 100 : 0;
              const barW  = maxValor > 0 ? (f.valor / maxValor) * 100 : 0;
              const color = rowColor(i);
              const isTop = i < 3;
              return (
                <button
                  key={f.nome}
                  onClick={() => setSelected(f)}
                  className="w-full grid grid-cols-[auto_1fr_auto_auto] gap-x-3 items-center py-2.5 hover:bg-slate-50/80 transition-colors text-left rounded-lg"
                >
                  <RankBadge rank={i + 1} />

                  {/* Nome + barra */}
                  <div className="min-w-0">
                    <p
                      className="text-xs font-bold leading-tight truncate mb-1"
                      style={{ color: isTop ? color : "#475569" }}
                    >
                      {f.nome.toUpperCase()}
                    </p>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${barW}%`, backgroundColor: color }}
                      />
                    </div>
                  </div>

                  {/* Valor */}
                  <p
                    className="text-xs font-semibold text-right whitespace-nowrap pr-2"
                    style={{ color: isTop ? color : "#64748b" }}
                  >
                    {formatCurrency(f.valor, true)}
                  </p>

                  {/* % */}
                  <p
                    className="text-xs font-bold text-right w-12"
                    style={{ color: isTop ? color : "#94a3b8" }}
                  >
                    {Math.round(pct)}%
                  </p>
                </button>
              );
            })}
          </div>

          {/* Botão ver mais */}
          {data.length > 7 && (
            <button
              onClick={() => setExpanded((v) => !v)}
              className="w-full py-2 text-xs text-brand-600 hover:text-brand-700 font-medium text-center border-t border-slate-100 mt-1 transition-colors"
            >
              {expanded ? "Ver menos ▲" : `Ver todos (${data.length}) ▼`}
            </button>
          )}
        </div>

        {/* ── Insight ── */}
        {top1 && (
          <div className="mx-5 mb-5 mt-3 flex items-center gap-2 bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2.5">
            <Lightbulb className="w-4 h-4 text-emerald-500 flex-shrink-0" />
            <p className="text-xs text-slate-600">
              <span className="font-semibold">Insight: </span>
              O fornecedor{" "}
              <span className="font-bold text-emerald-700">{top1.nome}</span>{" "}
              representa{" "}
              <span className="font-bold text-emerald-700">
                {total > 0 ? Math.round((top1.valor / total) * 100) : 0}%
              </span>{" "}
              do total comprado.
            </p>
          </div>
        )}
      </div>

      {/* ── Modal de detalhes ── */}
      <Modal
        open={!!selected}
        onClose={() => setSelected(null)}
        size="lg"
        title={selected && (
          <ModalEntityTitle
            icon={<Truck className="w-4 h-4" />}
            label={selected.nome}
            iconBg="bg-brand-50"
            iconColor="text-brand-600"
          />
        )}
      >
        {selected && (
          <PedidosModalContent pedidos={selected.pedidos} hideCols={["fornecedor"]} expandable />
        )}
      </Modal>
    </>
  );
}
